import lottie from 'lottie-web/build/player/esm/lottie_light.min.js';
import { scrollToElement, slideUp } from 'theme-utilities';
import { toggleLoadingState } from '../../../Button/src/modules/utils';
import { markFieldError } from '../../../Form/src/modules/fieldset';
import { lockForm, resetForm, unlockForm, validateForm } from '../../../Form/src/modules/validation';
import { getFormSubmitData } from './data';

const logErrorMessage = (message = '') => {
	console.error('An error occurred while sending the form data to HubSpot.', message);
};

export const onFormSubmit = async (form, event) => {
	if (!validateForm(form)) {
		// Stop propagation if the form is not valid
		// * This way Google Tag Manager's listener won't receive the bubbling submit event
		event.stopPropagation();
		return;
	}

	const submitBtn = form.querySelector('[type="submit"]');

	// Lock form and show feedback
	lockForm(form);
	toggleLoadingState(submitBtn);

	try {
		const result = await fetch(`${mpHubSpotAjax.url}?action=${mpHubSpotAjax.action}`, {
			method: 'POST',
			cache: 'no-cache',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				hs_route: form.action,
				hs_data: JSON.stringify(getFormSubmitData(form)),
			}),
		});

		const response = await result.json();

		unlockForm(form);
		toggleLoadingState(submitBtn, false);

		if (!response.success || response.data.responseCode !== 200) {
			throw new Error(response.data.responseBody);
		}

		const hubSpotResponse = response.data.responseBody;
		if (hubSpotResponse.errors) {
			hubSpotResponse.errors.forEach((err) => {
				logErrorMessage(`(${err.errorType}) ${err.message}`);

				switch (err.errorType) {
					case 'BLOCKED_EMAIL': {
						const emailField = form.querySelector('.form-field[data-field-type="email"]');
						emailField && markFieldError(emailField, 'blocked-email');
						break;
					}
				}
			});
		} else {
			resetForm(form);
			slideUp(form);

			if (hubSpotResponse.inlineMessage) {
				// * Option 1: Show a success message
				const container = form.closest('.hs_form');
				const submitMsg = document.createElement('div');

				const icon = document.createElement('div');
				const message = document.createElement('div');
				icon.className = 'submit__logo';
				message.className = 'submit__text';
				message.insertAdjacentHTML('beforeend', hubSpotResponse.inlineMessage);

				submitMsg.className = 'hs_form__submit-msg gradient';
				submitMsg.appendChild(icon);
				submitMsg.appendChild(message);
				container.prepend(submitMsg);

				// Scroll up
				scrollToElement('.hs_form__submit-msg');

				// Init animation after 1s due to scroll up duration
				setTimeout(() => {
					initSubmitMsgLogoAnimation(icon);
				}, 500);
			} else if (hubSpotResponse.redirectUri) {
				// * Option 2: Redirect to another URL
				location.assign(hubSpotResponse.redirectUri);
			}
		}
	} catch (error) {
		unlockForm(form);
		toggleLoadingState(submitBtn, false);
		logErrorMessage(error);
	}
};

const initSubmitMsgLogoAnimation = (container) => {
	lottie.loadAnimation({
		container: container,
		renderer: 'svg',
		loop: false,
		autoplay: true,
		animationData: {
			v: '5.9.0',
			fr: 29.9700012207031,
			ip: 0,
			op: 18.000000733155,
			w: 22,
			h: 21,
			nm: 'check-turq',
			ddd: 0,
			assets: [],
			layers: [
				{
					ddd: 0,
					ind: 1,
					ty: 4,
					nm: 'Shape Layer 1',
					sr: 1,
					ks: {
						o: { a: 0, k: 100, ix: 11 },
						r: { a: 0, k: 0, ix: 10 },
						p: { a: 0, k: [11.384, 10.95, 0], ix: 2, l: 2 },
						a: { a: 0, k: [-11.616, 37.95, 0], ix: 1, l: 2 },
						s: { a: 0, k: [100, 100, 100], ix: 6, l: 2 },
					},
					ao: 0,
					shapes: [
						{
							ty: 'gr',
							it: [
								{
									ind: 0,
									ty: 'sh',
									ix: 1,
									ks: {
										a: 0,
										k: {
											i: [
												[0, 0],
												[0, 0],
												[0, 0],
											],
											o: [
												[0, 0],
												[0, 0],
												[0, 0],
											],
											v: [
												[-18.182, 37.933],
												[-13.5, 42.5],
												[-5.05, 33.4],
											],
											c: false,
										},
										ix: 2,
									},
									nm: 'Path 1',
									mn: 'ADBE Vector Shape - Group',
									hd: false,
								},
								{
									ty: 'st',
									c: { a: 0, k: [0, 0.772549019608, 0.705882352941, 1], ix: 3 },
									o: { a: 0, k: 100, ix: 4 },
									w: { a: 0, k: 2, ix: 5 },
									lc: 2,
									lj: 1,
									ml: 4,
									bm: 0,
									nm: 'Stroke 1',
									mn: 'ADBE Vector Graphic - Stroke',
									hd: false,
								},
								{
									ty: 'tr',
									p: { a: 0, k: [0, 0], ix: 2 },
									a: { a: 0, k: [0, 0], ix: 1 },
									s: { a: 0, k: [100, 100], ix: 3 },
									r: { a: 0, k: 0, ix: 6 },
									o: { a: 0, k: 100, ix: 7 },
									sk: { a: 0, k: 0, ix: 4 },
									sa: { a: 0, k: 0, ix: 5 },
									nm: 'Transform',
								},
							],
							nm: 'Shape 1',
							np: 3,
							cix: 2,
							bm: 0,
							ix: 1,
							mn: 'ADBE Vector Group',
							hd: false,
						},
						{
							ty: 'tm',
							s: { a: 0, k: 0, ix: 1 },
							e: {
								a: 1,
								k: [
									{ i: { x: [0.705], y: [1] }, o: { x: [0.883], y: [0] }, t: 0, s: [0] },
									{ t: 18.000000733155, s: [100] },
								],
								ix: 2,
							},
							o: { a: 0, k: 0, ix: 3 },
							m: 1,
							ix: 2,
							nm: 'Trim Paths 1',
							mn: 'ADBE Vector Filter - Trim',
							hd: false,
						},
					],
					ip: 0,
					op: 18.000000733155,
					st: 0,
					bm: 0,
				},
			],
			markers: [],
		},
	});
};
